export interface GenerateOptions {
  rootName: string;
  readonly: boolean;
}
const reserved = new Set(
  "any unknown never string number boolean bigint symbol object null undefined void true false break case catch class const continue debugger default delete do else enum export extends false finally for function if import in instanceof new return super switch this throw try typeof var while with implements interface let package private protected public static yield await type declare abstract as asserts async constructor infer is keyof module namespace out override readonly require global unique using Array ReadonlyArray".split(
    " ",
  ),
);
const identifier = /^[A-Za-z_$][A-Za-z0-9_$]*$/;
const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export function generateTypes(input: string, options: GenerateOptions): string {
  const root = options.rootName.trim();
  if (!identifier.test(root) || reserved.has(root))
    throw new Error(
      "Choose a valid TypeScript name, such as ApiResponse. Use letters, numbers, _ or $, and start with a letter, _ or $.",
    );
  if (input.length > 250000)
    throw new Error(
      "Please use a smaller JSON sample (250,000 characters or fewer).",
    );
  if (!input.trim()) throw new Error("Paste a JSON sample to get started.");
  let value: unknown;
  try {
    value = JSON.parse(input);
  } catch (error) {
    throw new Error(
      `Invalid JSON. ${error instanceof Error ? error.message : "Check for missing quotes or commas."}`,
    );
  }
  const definitions: string[] = [];
  const usedNames = new Set([root]);
  let visited = 0;
  const prefix = options.readonly ? "readonly " : "";
  function nameFor(hint: string) {
    let base =
      hint
        .replace(/[^A-Za-z0-9]+/g, " ")
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0].toUpperCase() + part.slice(1))
        .join("") || "Value";
    if (!identifier.test(base) || reserved.has(base)) base = `Value${base}`;
    let name = base;
    let suffix = 2;
    while (usedNames.has(name)) name = `${base}${suffix++}`;
    usedNames.add(name);
    return name;
  }
  function check(depth: number, count: number) {
    visited += count;
    if (depth > 40 || visited > 25000)
      throw new Error(
        "This sample is too complex. Try fewer records or less nesting.",
      );
  }
  function objectType(
    objects: Record<string, unknown>[],
    name: string,
    depth: number,
  ): string {
    const keys = Array.from(
      new Set(
        objects.reduce<string[]>(
          (all, item) => all.concat(Object.keys(item)),
          [],
        ),
      ),
    );
    const lines = keys.map((key) => {
      const present = objects.filter((item) =>
        Object.prototype.hasOwnProperty.call(item, key),
      );
      const type = infer(
        present.map((item) => item[key]),
        `${name}_${key}`,
        depth + 1,
      );
      return `  ${prefix}${identifier.test(key) ? key : JSON.stringify(key)}${present.length < objects.length ? "?" : ""}: ${type};`;
    });
    definitions.push(
      `export interface ${name} {${lines.length ? "\n" + lines.join("\n") + "\n" : ""}}`,
    );
    return name;
  }
  function infer(samples: unknown[], hint: string, depth: number): string {
    check(depth, samples.length);
    if (!samples.length) return "unknown";
    const kinds: string[] = [];
    for (const sample of samples) {
      const kind =
        sample === null
          ? "null"
          : Array.isArray(sample)
            ? "array"
            : typeof sample;
      if (!kinds.includes(kind)) kinds.push(kind);
    }
    return kinds
      .map((kind) => {
        if (kind === "object")
          return objectType(samples.filter(isObject), nameFor(hint), depth);
        if (kind === "array") {
          const items = samples
            .filter(Array.isArray)
            .reduce<unknown[]>((all, array) => all.concat(array), []);
          return `${options.readonly ? "ReadonlyArray" : "Array"}<${infer(items, `${hint}Item`, depth + 1)}>`;
        }
        return kind;
      })
      .join(" | ");
  }
  if (isObject(value)) {
    check(0, 1);
    objectType([value], root, 0);
  } else {
    const type = infer([value], `${root}Value`, 0);
    definitions.push(`export type ${root} = ${type};`);
  }
  return (
    "// Generated from a JSON sample. Review before use; this is not runtime validation.\n\n" +
    definitions.join("\n\n") +
    "\n"
  );
}
